import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Mail, MessageCircle, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, CALL_OUTCOMES, LEAD_STATUSES, PRIORITIES, whatsappUrl } from "@/lib/fda";
import { useAuth } from "@/admin/AuthContext";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-6 border-b border-border py-2 text-sm last:border-0">
    <span className="font-semibold text-forest-soft">{label}</span>
    <span className="text-right text-forest">{value === null || value === undefined || value === "" ? "—" : String(value)}</span>
  </div>
);

const Card = ({ title, children, testid }) => (
  <section className="rounded-2xl border border-border bg-white p-6" data-testid={testid}>
    <h2 className="mb-4 font-display text-lg font-bold text-forest">{title}</h2>
    {children}
  </section>
);

export default function LeadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user.role !== "AGENT";
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [agents, setAgents] = useState([]);
  const [note, setNote] = useState("");
  const [call, setCall] = useState({ outcome: "", notes: "", next_callback_at: "" });
  const [cb, setCb] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/leads/${id}`); setD(data); setErr(""); }
    catch (e) { setErr(apiError(e.response?.data?.detail) || "Unable to load lead"); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) api.get("/agents").then(({ data }) => setAgents(data)).catch(() => {}); }, [isAdmin]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Action failed"); }
  };

  if (err) return <p className="rounded-xl bg-destructive/10 p-4 font-semibold text-destructive" data-testid="lead-detail-error">{err}</p>;
  if (!d) return <p className="text-sm text-forest-soft">Loading…</p>;

  const l = d.lead;
  const crmWaMessage = `Hi ${l.first_name}, this is Freedom Debt Advice regarding your enquiry (reference ${l.lead_reference}). Is now a good time to talk?`;

  return (
    <div data-testid="lead-detail-page">
      <Link to="/admin/leads" className="text-xs font-bold uppercase tracking-wider text-terracotta">&larr; All leads</Link>
      <div className="mt-3 flex flex-wrap items-center gap-4">
        <h1 className="font-display text-4xl font-extrabold text-forest" data-testid="lead-detail-reference">{l.lead_reference}</h1>
        <span className="rounded-full bg-forest px-3 py-1.5 text-xs font-bold text-sand" data-testid="lead-detail-status">{l.lead_status}</span>
        <span className="rounded-full border-2 border-border px-3 py-1.5 text-xs font-bold text-forest" data-testid="lead-detail-priority">{l.lead_priority}</span>
        {l.possible_duplicate && <span className="rounded-full bg-yellow-100 px-3 py-1.5 text-xs font-bold text-yellow-800" data-testid="duplicate-flag">Possible duplicate</span>}
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <a href={`tel:${l.phone}`} data-testid="call-customer-button"
          className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-3 text-xs font-extrabold uppercase tracking-wider text-sand">
          <PhoneCall className="h-4 w-4" /> Call customer
        </a>
        <a href={whatsappUrl(l.phone.replace(/^0/, "44").replace(/^\+/, ""), crmWaMessage)} target="_blank" rel="noopener noreferrer"
          data-testid="whatsapp-customer-button"
          className="inline-flex items-center gap-2 rounded-full bg-moss px-5 py-3 text-xs font-extrabold uppercase tracking-wider text-white">
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </a>
        <a href={`mailto:${l.email}?subject=${encodeURIComponent(`Your enquiry ${l.lead_reference}`)}`} data-testid="email-customer-button"
          className="inline-flex items-center gap-2 rounded-full border-2 border-border bg-white px-5 py-3 text-xs font-extrabold uppercase tracking-wider text-forest">
          <Mail className="h-4 w-4" /> Email
        </a>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <Card title="Customer" testid="customer-card">
          <Row label="First name" value={l.first_name} />
          <Row label="Last name" value={l.last_name} />
          <Row label="Phone" value={l.phone} />
          <Row label="Email" value={l.email} />
          <Row label="Preferred contact time" value={l.preferred_contact_time} />
          <Row label="Created" value={new Date(l.created_at).toLocaleString("en-GB")} />
        </Card>

        <Card title="Assessment" testid="assessment-card">
          <Row label="Situation" value={l.situation} />
          <Row label="Debt range" value={l.debt_range} />
          <Row label="Number of debts" value={l.creditor_count} />
          <Row label="Debt types" value={(l.debt_types || []).join(", ")} />
          <Row label="Other debt description" value={l.debt_types_other} />
          <Row label="Payment status" value={l.payment_status} />
          <Row label="Arrears duration" value={l.arrears_duration} />
          <Row label="Household income" value={l.household_income} />
          <Row label="Disposable income" value={l.disposable_income} />
          <Row label="Accommodation" value={l.accommodation_type} />
          <Row label="Existing debt help" value={l.existing_debt_help} />
          <Row label="Existing solution type" value={l.existing_solution_type} />
          <Row label="Primary goal" value={l.primary_goal} />
        </Card>

        <Card title="Consent" testid="consent-card">
          {d.consents.length === 0 && <p className="text-sm text-forest-soft">No consent records.</p>}
          {d.consents.map((c) => (
            <div key={c.id} className="mb-4 rounded-xl bg-sand p-4" data-testid={`consent-${c.consent_type}`}>
              <p className="text-xs font-bold uppercase tracking-wider text-terracotta">{c.consent_type}</p>
              <p className="mt-2 text-xs leading-relaxed text-forest-soft">{c.consent_text}</p>
              <p className="mt-2 text-[11px] font-semibold text-forest-soft">
                Version {c.consent_version} · {new Date(c.timestamp).toLocaleString("en-GB")}
                {c.channels?.length ? ` · Channels: ${c.channels.join(", ")}` : ""}
              </p>
            </div>
          ))}
        </Card>

        <Card title="Attribution" testid="attribution-card">
          <Row label="Source" value={l.source} />
          <Row label="Medium" value={l.medium} />
          <Row label="Campaign" value={l.campaign} />
          <Row label="Content" value={l.content} />
          <Row label="Term" value={l.term} />
          <Row label="fbclid" value={l.fbclid} />
          <Row label="Landing page" value={l.landing_page} />
          <Row label="Referrer" value={l.referrer} />
          <Row label="IP address" value={l.ip_address} />
        </Card>

        <Card title="CRM management" testid="crm-card">
          <label className="text-xs font-bold uppercase tracking-wider text-forest-soft">Status</label>
          <select data-testid="status-select" value={l.lead_status}
            onChange={(e) => act(() => api.patch(`/leads/${id}/status`, { lead_status: e.target.value }), "Status updated")}
            className="mt-2 w-full rounded-lg border-2 border-border px-3 py-2 text-sm">
            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-forest-soft">Priority</label>
          <select data-testid="priority-select" value={l.lead_priority}
            onChange={(e) => act(() => api.patch(`/leads/${id}/priority`, { lead_priority: e.target.value }), "Priority updated")}
            className="mt-2 w-full rounded-lg border-2 border-border px-3 py-2 text-sm">
            {PRIORITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          {isAdmin && (
            <>
              <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-forest-soft">Assigned agent</label>
              <select data-testid="assign-agent-select" value={l.assigned_agent || ""}
                onChange={(e) => act(() => api.patch(`/leads/${id}/assign`, { assigned_agent: e.target.value || null }), "Agent assigned")}
                className="mt-2 w-full rounded-lg border-2 border-border px-3 py-2 text-sm">
                <option value="">Unassigned</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </>
          )}
          <div className="mt-4 space-y-1 text-xs text-forest-soft">
            <p>Call attempts: <strong data-testid="call-attempts">{l.call_attempts}</strong></p>
            <p>Last contacted: {l.last_contacted_at ? new Date(l.last_contacted_at).toLocaleString("en-GB") : "—"}</p>
            <p>Next callback: {l.next_callback_at ? new Date(l.next_callback_at).toLocaleString("en-GB") : "—"}</p>
          </div>
        </Card>

        <Card title="Record a call" testid="record-call-card">
          <select data-testid="call-outcome-select" value={call.outcome}
            onChange={(e) => setCall({ ...call, outcome: e.target.value })}
            className="w-full rounded-lg border-2 border-border px-3 py-2 text-sm">
            <option value="">Select outcome…</option>
            {CALL_OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <textarea data-testid="call-notes-input" value={call.notes} placeholder="Call notes (required if outcome is Other)"
            onChange={(e) => setCall({ ...call, notes: e.target.value })} rows={3}
            className="mt-3 w-full rounded-lg border-2 border-border px-3 py-2 text-sm" />
          <label className="mt-3 block text-xs font-bold uppercase tracking-wider text-forest-soft">Next callback (optional)</label>
          <input type="datetime-local" data-testid="call-next-callback-input" value={call.next_callback_at}
            onChange={(e) => setCall({ ...call, next_callback_at: e.target.value })}
            className="mt-2 w-full rounded-lg border-2 border-border px-3 py-2 text-sm" />
          <button data-testid="record-call-button" disabled={!call.outcome}
            onClick={() => act(async () => {
              await api.post(`/leads/${id}/calls`, {
                outcome: call.outcome, notes: call.notes || null,
                next_callback_at: call.next_callback_at ? new Date(call.next_callback_at).toISOString() : null,
              });
              setCall({ outcome: "", notes: "", next_callback_at: "" });
            }, "Call recorded")}
            className="mt-4 w-full rounded-full bg-forest px-5 py-3 text-xs font-extrabold uppercase tracking-wider text-sand disabled:opacity-50">
            Record call outcome
          </button>

          <label className="mt-6 block text-xs font-bold uppercase tracking-wider text-forest-soft">Create callback task</label>
          <input type="datetime-local" data-testid="callback-input" value={cb} onChange={(e) => setCb(e.target.value)}
            className="mt-2 w-full rounded-lg border-2 border-border px-3 py-2 text-sm" />
          <button data-testid="create-callback-button" disabled={!cb}
            onClick={() => act(async () => {
              await api.post("/callbacks", { lead_id: id, callback_at: new Date(cb).toISOString(), notes: null });
              setCb("");
            }, "Callback created")}
            className="mt-3 w-full rounded-full border-2 border-forest px-5 py-3 text-xs font-extrabold uppercase tracking-wider text-forest disabled:opacity-50">
            Create callback
          </button>
        </Card>

        <Card title="Internal notes" testid="notes-card">
          <textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            placeholder="Internal note — never shown to the customer"
            className="w-full rounded-lg border-2 border-border px-3 py-2 text-sm" />
          <button data-testid="add-note-button" disabled={!note.trim()}
            onClick={() => act(async () => { await api.post(`/leads/${id}/notes`, { note }); setNote(""); }, "Note added")}
            className="mt-3 rounded-full bg-terracotta px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider text-white disabled:opacity-50">
            Add note
          </button>
          <div className="mt-5 space-y-3">
            {(l.notes || []).slice().reverse().map((n) => (
              <div key={n.id} className="rounded-xl bg-sand p-3" data-testid="note-item">
                <p className="text-sm text-forest">{n.note}</p>
                <p className="mt-1 text-[11px] font-semibold text-forest-soft">{n.author} · {new Date(n.timestamp).toLocaleString("en-GB")}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Call history" testid="call-history-card">
          {d.call_history.length === 0 && <p className="text-sm text-forest-soft">No calls recorded.</p>}
          {d.call_history.map((c) => (
            <div key={c.id} className="border-b border-border py-3 text-sm last:border-0" data-testid="call-history-item">
              <p className="font-semibold text-forest">Attempt {c.attempt_number} · {c.outcome}</p>
              <p className="text-xs text-forest-soft">{c.agent_name} · {new Date(c.timestamp).toLocaleString("en-GB")}</p>
              {c.notes && <p className="mt-1 text-xs text-forest-soft">{c.notes}</p>}
            </div>
          ))}
        </Card>

        <Card title="Timeline" testid="timeline-card">
          {d.history.map((h) => (
            <div key={h.id} className="border-b border-border py-2 text-xs last:border-0" data-testid="timeline-item">
              <span className="font-bold text-forest">{h.event_type}</span>
              {h.old_value || h.new_value ? <span className="text-forest-soft"> · {String(h.old_value ?? "—")} → {String(h.new_value ?? "—")}</span> : null}
              <span className="block text-forest-soft">{new Date(h.timestamp).toLocaleString("en-GB")}</span>
            </div>
          ))}
        </Card>

        <Card title="Callbacks" testid="lead-callbacks-card">
          {d.callbacks.length === 0 && <p className="text-sm text-forest-soft">No callbacks scheduled.</p>}
          {d.callbacks.map((c) => (
            <div key={c.id} className="border-b border-border py-2 text-sm last:border-0">
              {new Date(c.callback_at).toLocaleString("en-GB")} · <strong>{c.status}</strong>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
